import { addDays, diffDays, type DateStr } from "./date";

export type Phase = "menstruacao" | "folicular" | "ovulacao" | "lutea";

export const PHASE_LABEL: Record<Phase, string> = {
  menstruacao: "Menstruação",
  folicular: "Fase folicular",
  ovulacao: "Janela fértil",
  lutea: "Fase lútea"
};

export interface CycleEstimate {
  /** 1-based day of the current cycle (past `length` when the period is late). */
  day: number;
  length: number;
  periodLength: number;
  phase: Phase;
  /** First day of the current cycle. */
  start: DateStr;
  nextPeriod: DateStr;
  ovulation: DateStr;
  fertileStart: DateStr;
  fertileEnd: DateStr;
  /** Days past the expected period (0 when not late). */
  lateDays: number;
  /** Cycle days (1-based) of ovulation and of the fertile window. */
  ovulationDay: number;
  fertileStartDay: number;
}

/** A period overdue by more than this is taken as missing data: the estimate rolls forward. */
const MAX_LATE_DAYS = 14;

/** Phase of cycle day `day` (1-based): ovulation ~14 days before the next period, fertile window = it + the 5 days before. */
export function phaseOfDay(day: number, e: Pick<CycleEstimate, "periodLength" | "ovulationDay" | "fertileStartDay">): Phase {
  if (day <= e.periodLength) return "menstruacao";
  if (day < e.fertileStartDay) return "folicular";
  if (day <= e.ovulationDay) return "ovulacao";
  return "lutea";
}

/**
 * Where `today` falls given the start of the last period and the average lengths. A
 * future `lastStart` or one many cycles back projects whole cycles forward/back from it;
 * a period up to two weeks late stays "late" instead of silently starting a new cycle.
 */
export function estimateCycle(lastStart: DateStr, length: number, periodLength: number, today: DateStr): CycleEstimate {
  const elapsed = diffDays(lastStart, today);
  const late = elapsed > length && elapsed <= length + MAX_LATE_DAYS;
  const cycles = late ? 0 : Math.floor(elapsed / length);
  const start = addDays(lastStart, cycles * length);
  const day = diffDays(start, today) + 1;
  const ovulationDay = Math.max(1, length - 14);
  const fertileStartDay = Math.max(1, ovulationDay - 5);
  const nextPeriod = addDays(start, length);
  const parts = { periodLength, ovulationDay, fertileStartDay };
  // When the current cycle's ovulation is behind us, show the next one.
  const ovulationCycle = day > ovulationDay && !late ? nextPeriod : start;
  return {
    day,
    length,
    periodLength,
    phase: late ? "lutea" : phaseOfDay(day, parts),
    start,
    nextPeriod,
    ovulation: addDays(ovulationCycle, ovulationDay - 1),
    fertileStart: addDays(ovulationCycle, fertileStartDay - 1),
    fertileEnd: addDays(ovulationCycle, ovulationDay - 1),
    lateDays: late ? elapsed - length : 0,
    ovulationDay,
    fertileStartDay
  };
}
